class WebSocketManager {
  constructor() {
    this.connections = new Map(); // Store multiple connections
    this.globalListeners = new Map(); // Global event listeners
    this.reconnectTimers = new Map(); // Track reconnect timers
    this.heartbeatIntervals = new Map(); // Track heartbeat intervals
    this.maxReconnectAttempts = 5;
    this.baseReconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    this.heartbeatInterval = 30000; // 30 seconds
    this.connectionTimeout = 10000; // 10 seconds

    // Bind cleanup to page lifecycle events
    this.bindPageLifecycleEvents();
  }

  /**
   * Create or get existing WebSocket connection
   * @param {string} connectionId - Unique identifier for the connection
   * @param {string} url - WebSocket URL
   * @param {string} token - Authentication token
   * @param {Object} options - Connection options
   * @returns {WebSocket|null} WebSocket instance
   */
  connect(connectionId, url, token, options = {}) {
    // Clean up existing connection if any
    this.disconnect(connectionId);

    const connectionKey = connectionId;
    const wsUrl = `${url}?token=${token}`;

    try {
      //

      const ws = new WebSocket(wsUrl);
      const connectionData = {
        ws,
        url: wsUrl,
        token,
        reconnectAttempts: 0,
        listeners: new Map(),
        isIntentionallyClosed: false,
        lastPong: Date.now(),
        options: {
          autoReconnect: true,
          heartbeat: true,
          ...options
        }
      };

      // Set connection timeout
      const connectTimer = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      }, this.connectionTimeout);

      ws.onopen = () => {
        clearTimeout(connectTimer);
        connectionData.reconnectAttempts = 0;

        // Start heartbeat if enabled
        if (connectionData.options.heartbeat) {
          this.startHeartbeat(connectionKey);
        }

        // Notify global listeners
        this.notifyGlobalListeners('connection_open', { connectionId: connectionKey });
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle heartbeat responses
          if (data.type === 'pong') {
            connectionData.lastPong = Date.now();
            return;
          }

          // Notify connection-specific listeners
          const listeners = connectionData.listeners;
          listeners.forEach((callback, type) => {
            if (data.type === type || type === '*') {
              try {
                callback(data, connectionKey);
              } catch (error) {
              }
            }
          });

          // Notify global listeners
          this.notifyGlobalListeners(data.type, data, connectionKey);

        } catch (error) {
        }
      };

      ws.onclose = (event) => {
        clearTimeout(connectTimer);
        this.stopHeartbeat(connectionKey);

        if (!connectionData.isIntentionallyClosed && connectionData.options.autoReconnect) {
          this.scheduleReconnect(connectionKey, url, token, options);
        } else {
          this.connections.delete(connectionKey);
        }

        // Notify global listeners
        this.notifyGlobalListeners('connection_close', {
          connectionId: connectionKey,
          code: event.code,
          reason: event.reason
        });
      };

      ws.onerror = (error) => {
        this.notifyGlobalListeners('connection_error', {
          connectionId: connectionKey,
          error
        });
      };

      this.connections.set(connectionKey, connectionData);
      return ws;

    } catch (error) {
      return null;
    }
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnect(connectionId, url, token, options) {
    const connectionData = this.connections.get(connectionId);
    if (!connectionData) return;

    connectionData.reconnectAttempts++;

    if (connectionData.reconnectAttempts > this.maxReconnectAttempts) {
      this.connections.delete(connectionId);
      return;
    }

    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, connectionData.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    const timer = setTimeout(() => {
      this.reconnectTimers.delete(connectionId);
      if (this.connections.has(connectionId)) {
        this.connect(connectionId, url, token, options);
      }
    }, delay);

    this.reconnectTimers.set(connectionId, timer);
  }

  /**
   * Start heartbeat for connection
   */
  startHeartbeat(connectionId) {
    this.stopHeartbeat(connectionId); // Clear existing heartbeat

    const interval = setInterval(() => {
      const connectionData = this.connections.get(connectionId);
      if (!connectionData || connectionData.ws.readyState !== WebSocket.OPEN) {
        this.stopHeartbeat(connectionId);
        return;
      }

      // Check if we received pong recently
      if (Date.now() - connectionData.lastPong > this.heartbeatInterval * 2) {
        connectionData.ws.close();
        return;
      }

      // Send ping
      try {
        connectionData.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      } catch (error) {
      }
    }, this.heartbeatInterval);

    this.heartbeatIntervals.set(connectionId, interval);
  }

  /**
   * Stop heartbeat for connection
   */
  stopHeartbeat(connectionId) {
    const interval = this.heartbeatIntervals.get(connectionId);
    if (interval) {
      clearInterval(interval);
      this.heartbeatIntervals.delete(connectionId);
    }
  }

  /**
   * Subscribe to events for a specific connection
   */
  subscribe(connectionId, eventType, callback) {
    const connectionData = this.connections.get(connectionId);
    if (!connectionData) {
      return false;
    }

    connectionData.listeners.set(eventType, callback);
    return true;
  }

  /**
   * Unsubscribe from events for a specific connection
   */
  unsubscribe(connectionId, eventType) {
    const connectionData = this.connections.get(connectionId);
    if (!connectionData) return false;

    const removed = connectionData.listeners.delete(eventType);
    if (removed) {
    }
    return removed;
  }

  /**
   * Subscribe to global events (across all connections)
   */
  subscribeGlobal(eventType, callback) {
    if (!this.globalListeners.has(eventType)) {
      this.globalListeners.set(eventType, new Set());
    }
    this.globalListeners.get(eventType).add(callback);
  }

  /**
   * Unsubscribe from global events
   */
  unsubscribeGlobal(eventType, callback) {
    const listeners = this.globalListeners.get(eventType);
    if (listeners) {
      const removed = listeners.delete(callback);
      if (listeners.size === 0) {
        this.globalListeners.delete(eventType);
      }
      if (removed) {
      }
      return removed;
    }
    return false;
  }

  /**
   * Notify global listeners
   */
  notifyGlobalListeners(eventType, data, connectionId = null) {
    const listeners = this.globalListeners.get(eventType);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data, connectionId);
        } catch (error) {
        }
      });
    }
  }

  /**
   * Send message to specific connection
   */
  send(connectionId, message) {
    const connectionData = this.connections.get(connectionId);
    if (!connectionData || connectionData.ws.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
      connectionData.ws.send(messageStr);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Disconnect specific connection
   */
  disconnect(connectionId) {
    const connectionData = this.connections.get(connectionId);
    if (!connectionData) return false;

    // Mark as intentionally closed to prevent reconnection
    connectionData.isIntentionallyClosed = true;

    // Clear timers
    const reconnectTimer = this.reconnectTimers.get(connectionId);
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      this.reconnectTimers.delete(connectionId);
    }

    this.stopHeartbeat(connectionId);

    // Close WebSocket
    if (connectionData.ws) {
      connectionData.ws.close(1000, 'Intentional disconnect');
    }

    // Clear listeners
    connectionData.listeners.clear();

    // Remove from connections
    this.connections.delete(connectionId);

    return true;
  }

  /**
   * Disconnect all connections
   */
  disconnectAll() {

    const connectionIds = Array.from(this.connections.keys());
    connectionIds.forEach(connectionId => {
      this.disconnect(connectionId);
    });

    // Clear global listeners
    this.globalListeners.clear();
  }

  /**
   * Get connection status
   */
  getConnectionStatus(connectionId) {
    const connectionData = this.connections.get(connectionId);
    if (!connectionData) {
      return { status: 'disconnected', exists: false };
    }

    const readyStateMap = {
      [WebSocket.CONNECTING]: 'connecting',
      [WebSocket.OPEN]: 'open',
      [WebSocket.CLOSING]: 'closing',
      [WebSocket.CLOSED]: 'closed'
    };

    return {
      status: readyStateMap[connectionData.ws.readyState] || 'unknown',
      exists: true,
      reconnectAttempts: connectionData.reconnectAttempts,
      listeners: connectionData.listeners.size,
      lastPong: connectionData.lastPong
    };
  }

  /**
   * Get all connection statuses
   */
  getAllConnectionStatuses() {
    const statuses = {};
    this.connections.forEach((data, connectionId) => {
      statuses[connectionId] = this.getConnectionStatus(connectionId);
    });
    return statuses;
  }

  /**
   * Bind page lifecycle events for cleanup
   */
  bindPageLifecycleEvents() {
    // Cleanup on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.disconnectAll();
      });

      // Cleanup on page visibility change (mobile background)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          //
          // Keep connections alive but reduce heartbeat frequency
        } else {
          //
          // Resume normal heartbeat frequency
        }
      });

      // Handle memory pressure (if supported)
      if ('memory' in performance) {
        setInterval(() => {
          const memInfo = performance.memory;
          const memUsageMB = memInfo.usedJSHeapSize / 1024 / 1024;

          // If memory usage is high, log warning
          // if (memUsageMB > 100) {
          //
          //
          // }
        }, 60000); // Check every minute
      }
    }
  }

  /**
   * Get memory usage statistics
   */
  getMemoryStats() {
    return {
      connections: this.connections.size,
      globalListeners: this.globalListeners.size,
      reconnectTimers: this.reconnectTimers.size,
      heartbeatIntervals: this.heartbeatIntervals.size,
      totalListeners: Array.from(this.connections.values())
        .reduce((sum, conn) => sum + conn.listeners.size, 0)
    };
  }
}

export default new WebSocketManager();